import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltCheckbox } from '@cae/cobalt-react/components/checkbox';
import { CobaltModal } from '@cae/cobalt-react/components/modal';
import * as React from 'react';

import type { PasswordResetModalProps } from './PasswordResetModal.types';

import { passwordReset } from '../helpers/passwordReset';
import {
  COUNTDOWN_INTERVAL_MS,
  COUNTDOWN_START,
  PASSWORD_RESET_STORAGE_KEY,
} from '../PasswordAndSecurity.utils';
import styles from './PasswordResetModal.module.css';

const PasswordResetModal = React.memo(
  ({
    isModalOpen,
    onClose,
    loginHint,
  }: Readonly<PasswordResetModalProps>): React.ReactElement => {
    const [counter, setCounter] = React.useState(COUNTDOWN_START);
    const [checked, setChecked] = React.useState(false);

    const handleCheckboxChange = React.useCallback((): void => {
      setChecked(prev => !prev);
    }, []);

    React.useEffect(() => {
      if (!isModalOpen) {
        setCounter(COUNTDOWN_START);
        setChecked(false);
        return;
      }
      const interval = setInterval(() => {
        setCounter(prevCounter => (prevCounter > 0 ? prevCounter - 1 : 0));
      }, COUNTDOWN_INTERVAL_MS);
      return () => clearInterval(interval);
    }, [isModalOpen]);

    React.useEffect(() => {
      if (counter === 0 && isModalOpen) {
        if (checked) localStorage.setItem(PASSWORD_RESET_STORAGE_KEY, 'true');
        passwordReset(loginHint);
        onClose();
      }
    }, [counter, checked, onClose, isModalOpen, loginHint]);

    return (
      <CobaltModal
        isOpen={isModalOpen}
        onClose={onClose}
        title="On your way to reset password"
        data-heap-id="password-reset-modal"
      >
        <div className={styles.container}>
          <p className={styles.countdown}>{`Opening in ${counter}...`}</p>
          <CobaltCheckbox
            id="profile-password-reset-do-not-show-again-checkbox"
            labelText="Do not show again"
            checked={checked}
            onChange={handleCheckboxChange}
            data-heap-id="profile-password-reset-do-not-show-again-checkbox"
          />
        </div>
        <CobaltActionBar
          variant='flat'
          position="right"
          mobile={false}
          primaryButton={
            <CobaltButton
              data-heap-id="profile-password-reset-cancel-btn"
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </CobaltButton>
          }
        />
      </CobaltModal>
    );
  }
);

PasswordResetModal.displayName = 'PasswordResetModal';
export { PasswordResetModal };
